import { ICellRendererParams } from 'ag-grid-community'
import columnRenderers, { PLACEHOLDER } from 'components/column-renderers'
import dateRenderer from 'components/column-renderers/date'
import { notificationSettingsCellRenderer } from 'components/column-renderers/notificationSettings'
import { createCohortsNamesColumn } from 'features/Cohorts'
import { getActionColumn } from 'features/UI'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { routes } from 'routes/routes'
import { OrganizationalUser, UserOrgTab, UserTypeLabels } from 'types'
import { getRoute, isDefined } from 'utils'
import { UserOrgAccessCodesCell } from '../components/UserOrgAccessCodesCell/UserOrgAccessCodesCell'

export function useUserOrgColumns() {
  const navigate = useNavigate()

  const handleEditUser = useCallback(
    (userId: number) => {
      const route = getRoute(routes.orgUserDetails, { userId, tab: UserOrgTab.Details })
      navigate(route)
    },
    [navigate],
  )

  const actions = useMemo(() => {
    return [
      {
        name: 'Edit',
        onClick: handleEditUser,
      },
    ]
  }, [handleEditUser])

  return useMemo(
    () => [
      { headerName: 'First Name', field: 'firstname', filter: 'agTextColumnFilter' },
      { headerName: 'Last Name', field: 'lastname', filter: 'agTextColumnFilter' },
      { headerName: 'Email', field: 'email', filter: 'agTextColumnFilter' },
      {
        headerName: 'Log-in',
        field: 'hasLoggedIn',
        minWidth: 100,
        cellRenderer: (props: ICellRendererParams<{ hasLoggedIn?: boolean }>) => {
          const hasLoggedIn = props.data?.hasLoggedIn
          if (!isDefined(hasLoggedIn)) return PLACEHOLDER
          return hasLoggedIn ? 'Yes' : 'No'
        },
      },
      {
        headerName: 'User Status',
        field: 'userStatus',
        cellRenderer: columnRenderers.capitalizeRenderer('userStatus'),
      },
      {
        headerName: 'Role',
        colId: 'type',
        cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
          const type = props.data?.type
          if (isDefined(type)) {
            const role = type[0]
            return UserTypeLabels[role] || PLACEHOLDER
          }
          return PLACEHOLDER
        },
      },
      createCohortsNamesColumn('cohortNames'),
      {
        headerName: 'Institutional Assess Codes',
        field: 'totalOfAccessCodes',
        minWidth: 250,
        cellRenderer: (props: ICellRendererParams<OrganizationalUser>) => {
          const ownedAccessCodes = props.data?.ownedAccessCodes
          if (ownedAccessCodes?.length) {
            return <UserOrgAccessCodesCell accessCodes={ownedAccessCodes} />
          }
          return PLACEHOLDER
        },
      },
      {
        headerName: 'Last Notification',
        field: 'lastNotificationSentAt',
        cellRenderer: dateRenderer('lastNotificationSentAt', 'MM/DD/YY'),
        minWidth: 200,
      },
      {
        headerName: 'Notification Settings',
        field: 'notificationDelivery',
        cellRenderer: notificationSettingsCellRenderer,
        minWidth: 280,
      },
      {
        headerName: 'Effective Date',
        cellRenderer: dateRenderer('minEffectiveDateOfAccessCodes'),
        colId: 'minEffectiveDateOfAccessCodes',
      },
      {
        headerName: 'Expiration Date',
        cellRenderer: dateRenderer('maxExpiryDateOfAccessCodes'),
        colId: 'maxExpiryDateOfAccessCodes',
      },
      getActionColumn(actions),
    ],
    [actions],
  )
}
